import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@vwfs-bronson/bronson-react';
import { TanAuthentication } from '@vwfs-its/sf-sac-frontend';
import { useApplicationState } from '../../App.state';
import { checkTanValidity, tanAndTokenCreation } from '../../api/authentication';
import { appConfig } from '../../../config';
import { brandNames } from '../../../shared/common';
import { RouterHelpers } from '../../router/router-helpers';
import { shouldAuthTransaction, createTransactionAndToken } from '../../api/transaction';
import { setSearchParams } from '../../utils/set-search-params';

export const SaveAndContinue = ({
  onAuthorizeComplete,
  onError
}: {
  onAuthorizeComplete: () => void;
  onError: () => void;
}): JSX.Element => {
  const navigate = useNavigate();
  const { state, dispatch } = useApplicationState();

  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [shouldShowTanAuthentication, setShouldShowTanAuthentication] = useState(false);
  const [initialTanResponse, setInitialTanResponse] = useState<any>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!state.sessionToken && !state.transactionId) {
      setShowSpinner(true);
      createTransactionAndToken(state)
        .then((res) => {
          dispatch({ type: 'updateSessionToken', sessionToken: res.token });
          dispatch({ type: 'updateTransactionId', transactionId: res.transaction.id });
          setSearchParams({ transactionId: res.transaction.id, token: res.token });
        })
        .finally(() => {
          if (isMountedRef.current) {
            setShowSpinner(false);
          }
        });

      return;
    }

    if (!state.sessionToken && state.transactionId && !shouldShowTanAuthentication) {
      const legalEntity = new URLSearchParams(window.location.search).get('LegalEntity');

      setShowSpinner(true);

      shouldAuthTransaction(state.transactionId)
        .then(({ shouldAuth }) => {
          tanAndTokenCreation(state.transactionId, shouldAuth, legalEntity)
            .then((res) => {
              const newTransaction = res?.data?.newTransaction;
              const token = res?.data?.token;

              if ((!shouldAuth || newTransaction) && token) {
                dispatch({ type: 'updateSessionToken', sessionToken: token });
                setSearchParams({ token });
                return;
              }

              setInitialTanResponse(res);
              setShouldShowTanAuthentication(shouldAuth);
            })
            .catch((e) => {
              if (e?.response?.status === 410) navigate(RouterHelpers.getLinkExpiredPagePath());
              else navigate(RouterHelpers.get499ErrorPagePath());
              onError && onError();
            })
            .finally(() => {
              setShowSpinner(false);
            });
        });
    }
  }, [state.sessionToken, state.transactionId, shouldShowTanAuthentication]);



  const sendSMS = async () => {
    if (initialTanResponse) {
      const res = initialTanResponse;
      setInitialTanResponse(null);
      return res;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const legalEntity = urlParams.get('LegalEntity');
    setShowSpinner(true);

    try {
      const res = await tanAndTokenCreation(state.transactionId, true, legalEntity);
      const newTransaction = res?.data?.newTransaction;
      const token = res?.data?.token;

      if (newTransaction && token) {
        dispatch({ type: 'updateSessionToken', sessionToken: token });
        setSearchParams({ transactionId: state.transactionId, token });
      }

      return res;
    } catch (e: any) {
      if (e?.response?.status === 410) navigate(RouterHelpers.getLinkExpiredPagePath());
      else navigate(RouterHelpers.get499ErrorPagePath());
      onError && onError();
      return e?.response;
    } finally {
      if (isMountedRef.current) {
        setShowSpinner(false);
      }
    }
  };

  const authorizeTransaction = async (tan: string, transactionId: string) => {
    setShowSpinner(true);
    try {
      const { data } = await checkTanValidity(transactionId, tan);

      if (data.status === 'ERROR') {
        return 'Invalid TAN';
      }
      if (!data.token) {
        throw new Error('Missing token after TAN validation');
      }

      dispatch({ type: 'updateSessionToken', sessionToken: data.token });
      setSearchParams({ token: data.token });

      onAuthorizeComplete();

      return data;
    } finally {
      if (isMountedRef.current) {
        setShowSpinner(false);
      }
    }
  };

  return (
    <>
      {showSpinner && <Spinner fullPage />}

      {shouldShowTanAuthentication && (
        <TanAuthentication
          version={1}
          transactionId={state.transactionId}
          productName={brandNames[appConfig.brandName]}
          externalLinkExpiryMessage={false}
          hideLinkExpiryButton={false}
          visible
          onError={(e: any) => {
            navigate(RouterHelpers.get499ErrorPagePath());
            onError && onError();
          }}
          customSendRequestTan={sendSMS}
          customSendRequestData={authorizeTransaction}
        />
      )}
    </>
  );
};
